import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import useAuth from '@/components/UserContext';
import Button from '@/components/Button';
import IcCheck from '@/assets/icons/Check.svg?react';
import styles from './OnboardingProgress.module.scss';

type StepId = 'phone' | 'kyc';

interface Step {
  id: StepId;
  label: string;
  cta: string;
  doneLabel: string;
  to: string;
  done: boolean;
}

const OnboardingProgress = () => {
  const { t } = useTranslation(['common']);
  const { userData } = useAuth();

  if (!userData.id) {
    return null;
  }

  const phoneDone = Boolean(userData.phone?.number);
  const kycDone = userData.kyc;
  const verifiedLabel = t('common:dashboard.onboarding.verified');

  const steps: Step[] = [
    {
      id: 'phone',
      label: t('common:dashboard.onboarding.steps.phone'),
      cta: t('common:dashboard.onboarding.steps.phoneCta'),
      doneLabel: verifiedLabel,
      to: '/account/profile/verify-phone',
      done: phoneDone,
    },
    {
      id: 'kyc',
      label: t('common:dashboard.onboarding.steps.kyc'),
      cta: t('common:dashboard.onboarding.steps.kycCta'),
      doneLabel: verifiedLabel,
      to: '/kyc',
      done: kycDone,
    },
  ];

  const total = steps.length;
  const completed = steps.filter((step) => step.done).length;

  if (completed === total) {
    return null;
  }

  const progress = (completed / total) * 100;

  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <div className={styles.heading}>
          <h2 className={styles.title}>
            {t('common:dashboard.onboarding.title')}
          </h2>
          <div className={styles.subtitle}>
            {t('common:dashboard.onboarding.subtitle', {
              completed,
              total,
            })}
          </div>
        </div>
        <div
          className={styles.progress}
          role="progressbar"
          aria-label={t('common:dashboard.onboarding.title')}
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={completed}
        >
          <span
            className={styles.progressBar}
            style={{ width: `${progress}%` }}
          />
        </div>
      </header>
      <ul className={styles.list}>
        {steps.map((step) => (
          <li
            key={step.id}
            className={classNames(styles.item, {
              [styles.itemDone]: step.done,
            })}
          >
            <span className={styles.check} aria-hidden="true">
              {step.done ? <IcCheck /> : <span className={styles.pendingDot} />}
            </span>
            <span className={styles.label}>{step.label}</span>
            <span className={styles.action}>
              {step.done ? (
                <span className={styles.doneTag}>{step.doneLabel}</span>
              ) : (
                <Button as="link" size="slim" variant="secondary" to={step.to}>
                  {step.cta}
                </Button>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default OnboardingProgress;
